import { useState, useRef, useEffect } from 'react';
import { Button, toast } from '@ponzinomics/ui';
import { useMemeConfig, useUpdateMemeConfig, useUploadCharacterImage } from '../../hooks/use-meme-config';

interface CharacterConfig {
  imageUrl: string | null;
  imageFile: File | null;
  description: string;
}

const defaultConfig: CharacterConfig = {
  imageUrl: null,
  imageFile: null,
  description: '',
};

export function MemeGeneratorPage(): React.ReactElement {
  const { data: savedConfig, isLoading: isLoadingConfig } = useMemeConfig();
  const updateConfig = useUpdateMemeConfig();
  const uploadImage = useUploadCharacterImage();

  const [config, setConfig] = useState<CharacterConfig>(defaultConfig);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state when API config loads
  useEffect(() => {
    if (savedConfig) {
      setConfig({
        imageUrl: savedConfig.characterImageUrl,
        imageFile: null,
        description: savedConfig.characterDescription,
      });
    }
  }, [savedConfig]);

  const handleSave = async (): Promise<void> => {
    try {
      let imageUrl = config.imageUrl;

      // If there's a new file to upload, upload it first
      if (config.imageFile) {
        const result = await uploadImage.mutateAsync(config.imageFile);
        imageUrl = result.url;
      }

      await updateConfig.mutateAsync({
        characterImageUrl: imageUrl,
        characterDescription: config.description,
      });
      toast({ title: 'Character saved' });
    } catch {
      toast({ title: 'Failed to save character', variant: 'destructive' });
    }
  };

  const handleFileSelect = (file: File): void => {
    if (file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setConfig({ ...config, imageUrl, imageFile: file });
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveImage = (): void => {
    if (config.imageUrl) {
      URL.revokeObjectURL(config.imageUrl);
    }
    setConfig({ ...config, imageUrl: null, imageFile: null });
  };

  const isSaving = updateConfig.isPending || uploadImage.isPending;
  const isValid = config.imageUrl && config.description.trim().length > 0;

  if (isLoadingConfig) {
    return (
      <div className="max-w-2xl">
        <div className="mb-10">
          <div className="h-8 w-48 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-2 h-4 w-80 animate-pulse rounded bg-white/[0.04]" />
        </div>
        <div className="space-y-8">
          <div className="h-32 w-32 animate-pulse rounded-lg bg-white/[0.06]" />
          <div className="h-px bg-white/[0.06]" />
          <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-2xl font-semibold text-white">Meme Generator</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure your character for AI-powered meme generation
        </p>
      </div>

      {/* Single unified section */}
      <div className="space-y-8">
        {/* Image Upload */}
        <div>
          <label className="mb-3 block text-sm font-medium text-white">Character Image</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className="hidden"
          />

          {config.imageUrl ? (
            <div className="group relative inline-block">
              <img
                src={config.imageUrl}
                alt="Character"
                className="h-32 w-32 rounded-lg object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <EditIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`inline-flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                isDragging
                  ? 'border-brand-400 bg-brand-400/5'
                  : 'border-white/[0.12] hover:border-white/[0.24]'
              }`}
            >
              <UploadIcon className="h-6 w-6 text-gray-500" />
              <span className="mt-2 text-xs text-gray-500">Upload</span>
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="h-px bg-white/[0.06]" />

        {/* Description */}
        <div>
          <label className="mb-3 block text-sm font-medium text-white">Character Description</label>
          <input
            type="text"
            value={config.description}
            onChange={(e) => setConfig({ ...config, description: e.target.value })}
            placeholder="A mischievous frog who loves crypto and speaks in gen-z slang..."
            maxLength={200}
            className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2.5 text-sm text-white transition-colors placeholder:text-gray-600 hover:border-white/[0.12] focus:outline-none"
          />
          <p className="mt-1.5 text-xs text-gray-600">
            Describe personality and traits for better meme generation
          </p>
        </div>

        {/* Separator */}
        <div className="h-px bg-white/[0.06]" />

        {/* Action */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-600">{!isValid && 'Add image and description to save'}</p>
          <Button onClick={handleSave} disabled={isSaving || !isValid} size="sm">
            {isSaving ? 'Saving...' : 'Save Character'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function UploadIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}
